import React, { useMemo } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from '@material-ui/core';
import VisibilityIcon from '@material-ui/icons/Visibility';
import EditIcon from '@material-ui/icons/Edit';
import { CustomTable } from '../../../components';
import {
  COLUMNS,
  EDITABLE_STATUSES,
  EMPTY_TABLE_MESSAGE,
  PAGE_SIZE_OPTIONS,
  STATUS_LABELS,
} from '../constants';
import type { AssistedAffiliationStatus, AssistedAffiliationTableProps } from '../types';
import useStyle from '../styles';

const AssistedAffiliationTable = ({
  requests,
  readOnly = false,
  loading = false,
  validatingRequestId = null,
  onEdit,
  totalElements = 0,
  onNextPage,
  onPageSizeChange,
}: AssistedAffiliationTableProps) => {
  const classes = useStyle();

  const chipClassByStatus: Record<AssistedAffiliationStatus, string> = useMemo(() => ({
    DRAFT: classes.chipDraft,
    IN_PROGRESS: classes.chipInProgress,
    PENDING_INFORMATION: classes.chipDraft,
    COMMERCE_CREATED: classes.chipSuccess,
    REQUEST_CANCELLED: classes.chipError,
    CREATION_ERROR: classes.chipError,
  }), [classes]);

  // Referencia estable: ver `CustomTableProps.data`.
  const rows = useMemo(() => requests.map((request) => ({
    id: request.id,
    active: true,
    registryNumber: request.registryNumber,
    createdAt: new Date(request.createdAt).getTime(),
    identification: request.identification,
    commercialName: request.commercialName,
    legalRepresentative: request.legalRepresentative,
    status: (
      <Chip
        label={STATUS_LABELS[request.status]}
        size="small"
        className={`${classes.chip} ${chipClassByStatus[request.status]}`}
      />
    ),
    bhdExecutive: request.bhdExecutive,
    commercialAssignee: request.commercialAssignee,
    actions: (
      <Box display="flex" justifyContent="center">
        <Tooltip title="Ver">
          <IconButton size="small" aria-label={`ver-${request.registryNumber}`}>
            <VisibilityIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Editar">
          <span>
            <IconButton
              size="small"
              aria-label={`editar-${request.registryNumber}`}
              disabled={
                readOnly ||
                !EDITABLE_STATUSES.includes(request.status) ||
                validatingRequestId === request.id
              }
              onClick={() => onEdit?.(request)}
            >
              {validatingRequestId === request.id ? (
                <CircularProgress size={18} />
              ) : (
                <EditIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    ),
  })), [requests, readOnly, validatingRequestId, onEdit, classes, chipClassByStatus]);

  // Sin esta condición el estado vacío parpadea mientras la consulta está en vuelo.
  if (loading) {
    return (
      <Box className={classes.emptyState}>
        <CircularProgress />
      </Box>
    );
  }

  if (requests.length === 0) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" gutterBottom>
          {EMPTY_TABLE_MESSAGE}
        </Typography>
      </Box>
    );
  }

  return (
    <CustomTable
      data={rows}
      columns={COLUMNS}
      readOnly={readOnly}
      totalElementsData={totalElements}
      onNext={onNextPage}
      onSelectPageSize={onPageSizeChange}
      rowsPerPageOptions={PAGE_SIZE_OPTIONS}
    />
  );
};

export default AssistedAffiliationTable;
